import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Organization } from '@app/interfaces/accounts_list';
import { MixpanelService } from '@app/services/mixpanel.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { UserService } from '@app/services/user/user.service';
import { PermissionService } from '@core/services/permission/permission.service';
import { getActiveOrganization } from '@utils/helpers';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { PermissionApiService } from '@app/infrastructure/permission/permission-api.service';
import { AccountService } from '@core/services/account/account.service';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { LoaderService } from '@app/services/loader.service';

interface LaunchError {
	errorText: string;
	errorDescription: string;
}

@Injectable({
	providedIn: 'root',
})
export class HostGuard implements CanActivate {
	constructor(
		private userService: UserService,
		private mixpanelService: MixpanelService,
		private permissionService: PermissionService,
		private permissionApiService: PermissionApiService,
		private organizationsService: OrganizationsService,
		private accountService: AccountService,
		private featureFlagManagerService: FeatureFlagManagerService,
		private loaderService: LoaderService,
	) {}

	public canActivate(): Observable<boolean> {
		const error = this.isPageLaunchingWithError();
		if (!!error) {
			return this.userService
				.requestLogin({
					step: 'error',
					...error,
				})
				.pipe(map(() => false));
		}
		this.loaderService.showLoader();
		return this.userService.isAuthenticated$.pipe(
			concatMap((isAuthenticated: boolean) => {
				if (!isAuthenticated) {
					return this.userService.requestLogin();
				}
				return this.resolve();
			}),
			tap(() => {
				this.mixpanelService.init();
				this.loaderService.hideLoader();
			}),
			map((user) => !!user),
		);
	}

	private isPageLaunchingWithError(): LaunchError | null {
		const url = new URL(location.href);
		const errorText = url.searchParams.get('error');
		const errorDescription = url.searchParams.get('error_description');
		if (errorText && errorDescription) {
			return {
				errorDescription,
				errorText,
			};
		}
		return null;
	}

	/**
	 * 0. Update user model (by requesting user$ from userService)
	 * 1. Init permissions request
	 * 2. Get organizations & update currently selected user organization
	 * @private
	 */
	private resolve(): Observable<boolean> {
		return this.userService.user$.pipe(
			switchMap(() =>
				this.featureFlagManagerService.flags$.pipe(
					filter((flags) => !!Object.keys(flags).length),
					take(1),
				),
			),
			switchMap(() => {
				const enableInitResources = this.featureFlagManagerService.isFlagEnabled(
					FEATURE_FLAGS.ENABLE_ENABLE_INITIAL_RESOURCES_BFF_ENDPOINT,
				);

				return enableInitResources ? this.initResources() : this.deprecatedInitProcess();
			}),
		);
	}

	private deprecatedInitProcess(): Observable<boolean> {
		return this.permissionService.initPermission().pipe(
			switchMap(() => this.organizationsService.getOrganizations()),
			map((organizations) => {
				const currentOrganization: Organization = getActiveOrganization(organizations);
				this.userService.updateUserOrganization(currentOrganization);
				return true;
			}),
			catchError((err) => {
				console.error(err);
				return of(false);
			}),
		);
	}

	private initResources(): Observable<boolean> {
		return this.organizationsService.getInitialResources().pipe(
			tap((resources) => {
				// update accounts
				this.accountService.initAccounts(resources.accounts);
				// update organizations
				this.organizationsService.updateOrganizations({ organizations: resources.organizations });
				const currentOrganization: Organization = getActiveOrganization(resources.organizations);
				this.userService.updateUserOrganization(currentOrganization);
				// update permissions
				this.permissionApiService.updatePermissions({
					permissions: resources.permissions,
					success: true,
				});

				return true;
			}),
			map(Boolean),
			switchMap(() => this.permissionService.initPermission()),
			switchMap(() => this.organizationsService.getOrganizations().pipe(map(Boolean))),
			catchError((err) => {
				console.error('Failed getInitialResources: ', err);
				return of(false);
			}),
		);
	}
}
